import React, { useState, useEffect } from 'react';
import { useFeathers } from '../../app/util';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { Application } from '@feathersjs/feathers';
import { useSelector } from 'react-redux';
import qs from 'qs';
import styled from 'styled-components';
import { CandidateRow } from './CandidateRow';
import { RaceRow } from './RaceRow';
import { 
  KeyboardArrowUp, 
  KeyboardArrowDown, 
  Check, 
  Close,
  Email,
  Phone,
  Home,
  Language,
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  Campaign
} from '@mui/icons-material';
import { IconButton, Button } from '@mui/material';
import { Race } from '../../app/feathers/races/Race';

import {
  CirclePhoto,
  DataDetailToolbar,
  TypeDropdown,
} from '../../components';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Collapse,
  Box,
  Typography,
  Chip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { Overlay, OverlayType } from '../../app/feathers/overlays/Overlay';

const OverlayTypeChip = styled(Chip)<{ overlaytype: OverlayType }>`
  background-color: ${props => {
    switch (props.overlaytype) {
      case 'edit':
        return '#E3F2FD';  // Pale blue
      case 'delete':
        return '#FFEBEE';  // Pale red
      case 'add':
        return '#E8F5E9';  // Pale green
      default:
        return '#F5F5F5';  // Pale grey
    }
  }};
  color: ${props => {
    switch (props.overlaytype) {
      case 'edit':
        return '#1565C0';  // Bold blue
      case 'delete':
        return '#C62828';  // Bold red
      case 'add':
        return '#2E7D32';  // Bold green
      default:
        return '#424242';  // Bold grey
    }
  }};
  font-weight: 600;
  text-transform: capitalize;
  border-radius: 4px;
  height: 24px;
  padding: 0 12px;
  border: 1px solid;
  border-color: ${props => {
    switch (props.overlaytype) {
      case 'edit':
        return '#1565C0';  // Bold blue
      case 'delete':
        return '#C62828';  // Bold red
      case 'add':
        return '#2E7D32';  // Bold green
      default:
        return '#424242';  // Bold grey
    }
  }};
  .MuiChip-label {
    padding: 0;
  }
`;

const getChangesSummary = (overlay: Overlay, serviceName: string, candidatesDeselected: boolean): string => {
  if (!overlay) return '';

  const serviceNamePretty = serviceName === 'races' ? 'race' : 'candidate';
  const addedCandidatesCount = (overlay.overlayDocument as Race | undefined)?.candidateSummary?.numCandidates ?? 0;
  const deletedCandidatesCount = (overlay.globalDocument as Race | undefined)?.candidateSummary?.numCandidates ?? 0;

  switch (overlay.overlayType) {
    case 'add':
      let baseAdd = `Create with ${overlay.numberOfChanges} fields`;
      if (serviceName === 'races' && !candidatesDeselected && addedCandidatesCount > 0) {
        baseAdd += ` and ${addedCandidatesCount} candidates`;
      }
      return baseAdd;
    case 'delete':
      let baseDelete = `Remove ${serviceNamePretty}`;
      if (serviceName === 'races' && !candidatesDeselected && deletedCandidatesCount > 0) {
        baseDelete += ` and ${deletedCandidatesCount} candidates`;
      }
      return baseDelete;
    case 'edit':
      if (!overlay.changes || overlay.changes.length === 0) return 'No changes';
      
      const changes = overlay.changes.map(change => change.changes);

      if (changes.length <= 4) {
        return changes.join(', ');
      }
      return `${changes.slice(0, 4).join(', ')} + ${changes.length - 4} more`;
  }
  return '';
};

const getContactIcon = (method: string) => {
  switch (method.toLowerCase()) {
    case 'email':
      return <Email fontSize="small" />;
    case 'phone':
      return <Phone fontSize="small" />;
    case 'social':
      return <Home fontSize="small" />;
    case 'form':
      return <Language fontSize="small" />;
    default:
      return <Campaign fontSize="small" />;
  }
};

const ValueDisplay: React.FC<{ value: any, fieldName?: string }> = ({ value, fieldName }) => {
  if(fieldName === 'contact' && Array.isArray(value)) {
    return (
      <Box sx={{ display: 'grid', gap: 1 }}>
        {value.map((contact: any) => (
          <Box 
            key={contact.method} 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'auto 1fr',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: 'text.secondary'
            }}>
              {getContactIcon(contact.method)}
            </Box>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {contact.value}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }
  const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
  const isIdLike = typeof value === 'string' && value.length > 10 && !value.includes(' ');

  if (isIdLike) {
    return (
      <Box
        component="code"
        sx={{
          fontFamily: 'monospace',
          padding: '2px 6px',
          borderRadius: '4px',
          border: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5',
          fontSize: '0.875rem'
        }}
      >
        {stringValue}
      </Box>
    );
  }

  return <span>{(stringValue || '').replace(/^"(.*)"$/, '$1')}</span>;
};

const ExpandedDetails: React.FC<{ 
  overlay: Overlay, 
  serviceName: string,
  candidatesDeselected?: boolean,
  onCandidatesSelectionChange?: (overlayId: string, selected: boolean) => void 
}> = ({ 
  overlay, 
  serviceName,
  candidatesDeselected = false,
  onCandidatesSelectionChange 
}) => {
  if (overlay.overlayType === 'delete') {
    return null;
  }

  const showCandidates = serviceName === 'races' && 
    overlay.overlayType !== 'edit' && 
    (overlay.overlayDocument as Race)?.candidateSummary?.numCandidates > 0;

  return (
    <TableRow>
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
        <Collapse in={true} timeout="auto" unmountOnExit>
          <Box sx={{ margin: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Field Name</TableCell>
                  {overlay.overlayType === 'edit' && <TableCell>Before</TableCell>}
                  <TableCell>{overlay.overlayType === 'edit' ? 'After' : 'Value'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overlay.overlayType === 'add' && overlay.changes?.map((change, index) => (
                  <TableRow key={index}>
                    <TableCell>{change.fieldName}</TableCell>
                    <TableCell><ValueDisplay value={change.afterValue} /></TableCell>
                  </TableRow>
                ))}
                {overlay.overlayType === 'edit' && overlay.changes?.map((change, index) => (
                  <TableRow key={index}>
                    <TableCell>{change.fieldName}</TableCell>
                    <TableCell><ValueDisplay value={change.beforeValue} fieldName={change.fieldName} /></TableCell>
                    <TableCell><ValueDisplay value={change.afterValue} fieldName={change.fieldName} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {showCandidates && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Checkbox
                    checked={!candidatesDeselected}
                    onChange={(e) => onCandidatesSelectionChange?.(overlay._id, e.target.checked)}
                    size="small"
                  />
                  <Typography variant="subtitle2">
                    Candidates ({(overlay.overlayDocument as Race).candidateSummary.numCandidates})
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 2 
                }}>
                  {(overlay.overlayDocument as Race).candidateSummary.names.map((name, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                        opacity: candidatesDeselected ? 0.5 : 1
                      }}
                    >
                      <CirclePhoto
                        photoPath={(overlay.overlayDocument as Race).candidateSummary.photos[index]}
                        hasVariants={(overlay.overlayDocument as Race).candidateSummary.photoHasVariants?.[index] === true}
                        size={'small'}
                      />
                      <Typography variant="body2">
                        {name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

const Edits: React.FC = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const user = useSelector((state: any) => state.user);
  const { page, status = 'pending', service = 'races' } = qs.parse(window.location.search, { ignoreQueryPrefix: true });

  const [loading, setLoading] = useState(true);
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [overlayCount, setOverlayCount] = useState<number | null>(null);
  const [displayError, setDisplayError] = useState<Error | undefined>(undefined);
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);
  const [confirmingAcceptIds, setConfirmingAcceptIds] = useState<string[]>([]);
  const [processingIds, setProcessingIds] = useState<string[]>([]);
  const [pageSize, setPageSize] = useState(100);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: 'accept' | 'reject';
  } | null>(null);
  const [triggerRefreshOverlays, setTriggerRefreshOverlays] = useState(false);
  const [deselectedCandidatesOverlays, setDeselectedCandidatesOverlays] = useState<Set<string>>(new Set());

  const loadOverlays = async (pageNum: number, statusFilter: string, serviceFilter: string, rowsPerPage: number = pageSize) => {
    setLoading(true);
    setSelectedRows(new Set());
    try {
      const $skip = Math.max((pageNum ? parseInt(String(pageNum)) : 1) - 1, 0) * rowsPerPage;
      
      const query: any = {
        serviceName: serviceFilter,
        $limit: rowsPerPage,
        $skip,
        $sort: {
          createdAt: 1
        },
        $includeFullDocuments: true,
        $populate: ['overlayOrganization']
      };

      // Only add status filter if not 'all'
      if (statusFilter && statusFilter !== 'all') {
        query.resolutionStatus = statusFilter;
      }

      const overlaysService = feathers.getService('overlays');
      const result = await overlaysService.find({
        query
      });

      setOverlays(result.data);
      setOverlayCount(result.total);
      setPageSize(rowsPerPage);
    } catch (error) {
      console.error('Error loading overlays:', error);
      setDisplayError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and when filters/page change
  useEffect(() => {
    if (feathers) {
      loadOverlays(
        page ? parseInt(page as string) : 1,
        status as string,
        service as string,
        pageSize
      );
    }
  }, [feathers, page, status, service, pageSize]);

  useEffect(() => {
    if (triggerRefreshOverlays && overlays.length === 0) {
      loadOverlays(page ? parseInt(page as string) : 1, status as string, service as string, pageSize);
      setTriggerRefreshOverlays(false);
    }
  }, [triggerRefreshOverlays, overlays]);

  const handleServiceChange = (newService: string) => {
    const newQuery = qs.stringify({ 
      ...qs.parse(window.location.search, { ignoreQueryPrefix: true }), 
      service: newService, 
      page: 1 
    });
    window.history.pushState({}, '', `${window.location.pathname}?${newQuery}`);
    loadOverlays(1, status as string, newService, pageSize);
  };

  const handleStatusChange = (newStatus: string) => {
    const newQuery = qs.stringify({ 
      ...qs.parse(window.location.search, { ignoreQueryPrefix: true }), 
      status: newStatus, 
      page: 1 
    });
    window.history.pushState({}, '', `${window.location.pathname}?${newQuery}`);
    loadOverlays(1, newStatus, service as string, pageSize);
  };

  const handlePageChange = (newPage: number) => {
    const newQuery = qs.stringify({ 
      ...qs.parse(window.location.search, { ignoreQueryPrefix: true }), 
      page: newPage 
    });
    window.history.pushState({}, '', `${window.location.pathname}?${newQuery}`);
    loadOverlays(newPage, status as string, service as string, pageSize);
  };

  const coreDocumentDeleted = (overlay: Overlay) => {
    return (['delete', 'edit'].includes(overlay.overlayType)) && !overlay.globalDocument;
  }

  const noEdits = (overlay: Overlay) => {
    return overlay.changes?.length === 0 && overlay.overlayType === 'edit';
  }

  const handleAcceptClick = (overlayId: string) => {
    const overlay = overlays.find(overlay => overlay._id === overlayId);
    if(!overlay) {
      return;
    }
    if(noEdits(overlay) || coreDocumentDeleted(overlay)) {
      // just accept the overlay
      handleAcceptConfirm(overlayId);
      return;
    } else {
      setConfirmingAcceptIds(prev => [...prev, overlayId]);
    }
  };

  const handleAcceptConfirm = async (overlayId: string) => {
    try {
      setProcessingIds(prev => [...prev, overlayId]);
      const overlay = overlays.find(overlay => overlay._id === overlayId);
      if(!overlay) {
        return;
      }
      const statusModified = (noEdits(overlay) || coreDocumentDeleted(overlay)) ? 'rejected' : 'accepted';
      const withCandidates = (service === 'races' && overlay.overlayType !== 'edit')
        ? ((deselectedCandidatesOverlays.has(overlayId) && overlay.overlayType === 'add') ? false : true)
        : false;

      const response = await feathers.getService('overlays').patch(overlayId, {
        resolutionStatus: statusModified,
        ...(withCandidates ? { candidates: true } : {})
      }, {
        query: {
          serviceName: service as string
        }
      });
      setConfirmingAcceptIds(prev => prev.filter(id => id !== overlayId));
      // remove this overlay from the list
      console.log(`Accepted overlay ${overlayId}`);
      setOverlays(prevOverlays => prevOverlays.filter(overlay => overlay._id !== overlayId));
    } catch (error) {
      console.error('Error accepting overlay:', error);
      setDisplayError(error as Error);
    } finally {
      setProcessingIds(prev => prev.filter(id => id !== overlayId));
    }
  };

  const handleRejectClick = async (overlayId: string) => {
    try {
      setProcessingIds(prev => [...prev, overlayId]);
      const overlay = overlays.find(overlay => overlay._id === overlayId);
      if(!overlay) {
        return;
      }
      const withCandidates = (service === 'races' && overlay.overlayType !== 'edit')
        ? true
        : false;
      const response = await feathers.getService('overlays').patch(overlayId, {
        resolutionStatus: 'rejected',
        ...(withCandidates ? { candidates: true } : {})
      }, {
        query: {
          serviceName: service as string
        }
      });
      console.log(`Removed overlay ${overlayId}`);
      setConfirmingAcceptIds(prev => prev.filter(id => id !== overlayId));
      // remove this overlay from the list
      setOverlays(prevOverlays => prevOverlays.filter(overlay => overlay._id !== overlayId));
    } catch (error) {
      console.error('Error rejecting overlay:', error);
      setDisplayError(error as Error);
    } finally {
      setProcessingIds(prev => prev.filter(id => id !== overlayId));
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = new Set(overlays.map(overlay => overlay._id));
      setSelectedRows(newSelected);
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id: string) => {
    setSelectedRows(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      return newSelected;
    });
  };

  const handleAcceptAll = () => {
    setConfirmDialog({
      open: true,
      type: 'accept'
    });
  };

  const handleRejectAll = () => {
    setConfirmDialog({
      open: true,
      type: 'reject'
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog) return;

    try {
      const overlayIds = Array.from(selectedRows);
      setProcessingIds(prev => [...prev, ...overlayIds]);

      await Promise.all(
        overlayIds.map(async (overlayId) => {
          if(confirmDialog.type === 'accept') {
            await handleAcceptConfirm(overlayId);
          } else {
            await handleRejectClick(overlayId);
          }
        })
      );

      setTriggerRefreshOverlays(true);
      
      // Close dialog and clear selection
      setConfirmDialog(null);
      setSelectedRows(new Set());
      setProcessingIds([]);
    } catch (error) {
      console.error('Error processing bulk action:', error);
      setDisplayError(error as Error);
    }
  };

  const handleCandidatesSelectionChange = (overlayId: string, selected: boolean) => {
    setDeselectedCandidatesOverlays(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.delete(overlayId);
      } else {
        newSet.add(overlayId);
      }
      return newSet;
    });
  };

  const columns = service === 'races' ? [
    'Race Name',
    'Office / District',
  ] : [
    'Candidate',
    'Race'
  ]

  return (
    <Wrapper>
      <DataDetailToolbar
        onBack={{ to: { pathname: `/home` }}}
        navTree={[
          {
            text: 'Home',
            to: { pathname: `/home` }
          },
          { text: 'Edits' }
        ]}
      />
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', marginTop: '12px', gap: '12px' }}>
        <TypeDropdown
          value={service}
          placeholder="Select service"
          options={[
            { type: 'races', title: 'Races' },
            { type: 'candidates', title: 'Candidates' },
          ]}
          onChange={handleServiceChange}
        />
        <TypeDropdown
          value={status || 'pending'}
          placeholder="Filter by status"
          options={[
            // { type: 'all', title: 'All' },
            { type: 'pending', title: 'Pending' },
            { type: 'accepted', title: 'Accepted' },
            { type: 'rejected', title: 'Rejected' },
          ]}
          onChange={handleStatusChange}
        />
      </div>

      <TableContainer component={Paper} style={{ marginTop: '12px' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={overlays.length > 0 && selectedRows.size === overlays.length}
                  indeterminate={selectedRows.size > 0 && selectedRows.size < overlays.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Type</TableCell>
              {columns.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
              <TableCell>Changes</TableCell>
              <TableCell width="140px">Actions</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          {selectedRows.size > 0 && (
            <TableHead>
              <TableRow>
                <TableCell colSpan={columns.length + 6} style={{ padding: '8px 16px' }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                      onClick={handleAcceptAll}
                      startIcon={<Check />}
                    >
                      Accept all
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleRejectAll}
                      startIcon={<Close />}
                    >
                      Reject all
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + 6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : overlays.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 6} align="center">
                  No edits found
                </TableCell>
              </TableRow>
            ) : overlays.map((overlay, rowIndex) => (
              <React.Fragment key={rowIndex}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedRows.has(overlay._id)}
                      onChange={() => handleSelectRow(overlay._id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" style={{ fontSize: '0.75rem' }}>
                        {overlay.overlayOrganization?.name || 'N/A'}
                      </Typography>
                      <Chip
                        label={overlay.resolutionStatus || 'pending'}
                        size="small"
                        sx={{
                          height: '20px',
                          maxWidth: 'fit-content',
                          '& .MuiChip-label': { 
                            fontSize: '0.75rem', 
                            py: 0,
                            px: 1 
                          },
                          bgcolor: overlay.resolutionStatus === 'accepted' 
                            ? 'rgba(76, 175, 80, 0.08)' 
                            : overlay.resolutionStatus === 'rejected'
                            ? 'rgba(211, 47, 47, 0.08)'
                            : 'rgba(0, 0, 0, 0.08)',
                          color: overlay.resolutionStatus === 'accepted'
                            ? '#2e7d32'
                            : overlay.resolutionStatus === 'rejected'
                            ? '#c62828'
                            : 'rgba(0, 0, 0, 0.6)',
                          border: 'none'
                        }}
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <OverlayTypeChip
                      label={overlay.overlayType || 'unknown'}
                      overlaytype={overlay.overlayType as OverlayType}
                      size="small"
                    />
                  </TableCell>
                  {service === 'races' ? (
                    <RaceRow overlay={overlay} />
                  ) : (
                    <CandidateRow overlay={overlay} />
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {getChangesSummary(overlay, service as string, deselectedCandidatesOverlays.has(overlay._id))}
                      </Typography>
                      {overlay.overlayType !== 'delete' && overlay.changes && overlay.changes.length > 0 && (
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => setExpandedRowIndex(expandedRowIndex === rowIndex ? null : rowIndex)}
                        >
                          {expandedRowIndex === rowIndex ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {['pending', 'rejected'].includes(overlay.resolutionStatus) && (
                        confirmingAcceptIds.includes(overlay._id) ? (
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => handleAcceptConfirm(overlay._id)}
                            startIcon={!processingIds.includes(overlay._id) && <Check />}
                            fullWidth
                            disabled={processingIds.includes(overlay._id)}
                            sx={{
                              textTransform: 'none',
                              minHeight: '32px',
                              position: 'relative'
                            }}
                          >
                            {processingIds.includes(overlay._id) ? (
                              <CircularProgress
                                size={20}
                                sx={{
                                  color: 'success.light',
                                  position: 'absolute',
                                  left: '50%',
                                  marginLeft: '-10px'
                                }}
                              />
                            ) : (
                              'Confirm'
                            )}
                          </Button>
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleAcceptClick(overlay._id)}
                              disabled={processingIds.includes(overlay._id)}
                              sx={{ 
                                color: 'success.main',
                                '&:hover': { backgroundColor: 'success.light' }
                              }}
                            >
                              <Check />
                            </IconButton>
                            {
                              (noEdits(overlay) || coreDocumentDeleted(overlay)) ? <></> : 
                              <IconButton
                                size="small"
                                onClick={() => handleRejectClick(overlay._id)}
                                disabled={processingIds.includes(overlay._id)}
                                sx={{ 
                                  color: 'error.main',
                                  '&:hover': { backgroundColor: 'error.light' },
                                  position: 'relative'
                                }}
                              >
                                {processingIds.includes(overlay._id) ? (
                                  <CircularProgress
                                    size={16}
                                    sx={{
                                      color: 'error.light',
                                      position: 'absolute'
                                    }}
                                  />
                                ) : (
                                  <Close />
                                )}
                              </IconButton>
                            }
                          </>
                        )
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                {expandedRowIndex === rowIndex && (
                  <ExpandedDetails 
                    overlay={overlay} 
                    serviceName={service as string}
                    candidatesDeselected={deselectedCandidatesOverlays.has(overlay._id)}
                    onCandidatesSelectionChange={handleCandidatesSelectionChange}
                  />
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={overlayCount || 0}
          page={page ? parseInt(page as string) - 1 : 0}
          onPageChange={(event, newPage) => handlePageChange(newPage + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => {
            const newPageSize = parseInt(event.target.value, 10);
            setPageSize(newPageSize);
            loadOverlays(1, status as string, service as string, newPageSize);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
        />
      </TableContainer>

      <Dialog
        open={Boolean(confirmDialog?.open)}
        onClose={() => setConfirmDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm {confirmDialog?.type === 'accept' ? 'Accept' : 'Reject'} All
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmDialog?.type === 'accept' ? 'accept' : 'reject'} {selectedRows.size} edit{selectedRows.size !== 1 ? 's' : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={confirmDialog?.type === 'accept' ? 'success' : 'error'}
            startIcon={confirmDialog?.type === 'accept' ? <Check /> : <Close />}
          >
            {confirmDialog?.type === 'accept' ? 'Accept' : 'Reject'} All
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(displayError)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        autoHideDuration={6000}
        onClose={() => setDisplayError(undefined)}
      >
        <Alert
          severity="error"
          onClose={() => setDisplayError(undefined)}
          elevation={6}
          variant="filled"
        >
          {displayError?.message ?? `${displayError}`}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  min-height: calc(100% - 24px * 2);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

export default Edits; 